import { type JSX, type ReactNode, useEffect, useMemo, useState } from 'react';

import type { DeviceProperties, StorageInfo } from '@runtime/agent.js';
import type { Engine } from '@runtime/engine.js';

import { type DeviceIdentity, getDeviceIdentity } from '../../../bridge/device.js';
import { BrowserAgentHost } from '../../../browser-agent-host.js';
import { DeviceIcon } from './icons.js';
import { LauncherModal } from './LauncherModal.js';

export interface DeviceInfoModalProps {
  /**
   * Live runtime engine. Optional — the Builder preview omits it so
   * admins can see the modal's layout without booting an engine. When
   * absent the dossier renders entirely with placeholder values (as if
   * `mdm === 'mobicontrol'`) and shows a "preview mode" hint.
   */
  engine?: Engine;
  onClose: () => void;
  /** When set, footer shows a "← Back" link that pops one modal off
   *  the navigation stack instead of dismissing entirely. */
  onBack?: () => void;
}

/**
 * Read-only device dossier. Pulls live data from `engine.agentHost`'s
 * optional `getDeviceProperties` capability.
 *
 * By default we surface only what the browser/WebView + the device's own
 * bridge (`getDeviceIdentity`) can reliably report — no fake IMEI, carrier, or
 * SSID. The MDM-only dossier (IMEI, SSID, serial, signal, carrier, VPN) is
 * gated behind BOTH Enterprise tier AND the MDM agent opt-in
 * (`engine.tier === 'enterprise'` && `engine.mdm === 'mobicontrol'`, the latter
 * sourced from `config.mdm`). On that path those fields render as placeholders
 * with an amber pip until the production SOTI JS Agent API host ships.
 */
export function DeviceInfoModal({
  engine,
  onClose,
  onBack,
}: DeviceInfoModalProps): JSX.Element {
  const [props, setProps] = useState<DeviceProperties | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [storage, setStorage] = useState<StorageInfo | null>(null);
  // Accurate identity straight from the device: the APK bridge supplies real
  // manufacturer+model, OS release, security patch, app build, and LAN IPv4;
  // a browser/preview host falls back to UA-derived values. Idempotent.
  const identity = useMemo(() => getDeviceIdentity(), []);

  useEffect(() => {
    // When no engine is wired (Builder preview), fall back to a
    // BrowserAgentHost so the modal still picks up everything
    // `navigator.userAgent` / `screen` / `navigator.connection` can
    // tell us. That's the same data the live launcher would show on
    // a no-MDM device — useful for "preview what my fleet sees."
    const host = engine?.agentHost ?? new BrowserAgentHost();
    const fetcher = host.getDeviceProperties;
    if (!fetcher) {
      setError('Device properties unavailable on this host.');
      return undefined;
    }
    let cancelled = false;
    fetcher
      .call(host)
      .then((p) => {
        if (!cancelled) setProps(p);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [engine]);

  // Richer filesystem + RAM breakdown via the agent's getStorageInfo
  // (SOTI-only; browser/preview hosts don't supply it, so the section
  // simply doesn't render there).
  useEffect(() => {
    const host = engine?.agentHost;
    const fetcher = host?.getStorageInfo;
    if (host === undefined || fetcher === undefined) {
      setStorage(null);
      return undefined;
    }
    let cancelled = false;
    fetcher.call(host).then(
      (s) => {
        if (!cancelled) setStorage(s);
      },
      () => {
        if (!cancelled) setStorage(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [engine]);

  const hasStorage =
    storage !== null &&
    (storage.internal !== undefined ||
      storage.external !== undefined ||
      storage.memory !== undefined);

  // Preview mode (no engine): show the full dossier with placeholders so
  // admins can preview what their MDM-enrolled fleet will see.
  // Only surface MDM-only fields (IMEI, SSID, serial, signal, carrier, VPN…)
  // when the tenant is genuinely on Enterprise AND has opted into the MDM agent
  // integration. Otherwise we show only what the browser/WebView + the device's
  // own bridge can reliably report — no fake placeholders for everyone else.
  const mdmIntegration = engine?.tier === 'enterprise' && engine?.mdm === 'mobicontrol';
  // Real device name (e.g. "Samsung SM-X528U"), not an OEM-vendor guess.
  const deviceModel = identity.deviceName ?? props?.model ?? props?.deviceName ?? 'Device';
  const osLabel = formatOsLabel(identity);
  const formFactor = props?.formFactor ? cap(props.formFactor) : '';
  const conn = props?.connection;

  return (
    <LauncherModal
      title="Device Info"
      onClose={onClose}
      testId="device-info-modal"
      {...(onBack && { onBack })}
    >
      <div className="device-info">
        <header className="device-info__hero">
          <div className="device-info__hero-icon" aria-hidden>
            <DeviceIcon />
          </div>
          <div>
            <div className="device-info__hero-title">{deviceModel}</div>
            <div className="device-info__hero-sub">
              {[osLabel === '—' ? null : osLabel, formFactor, props?.language]
                .filter(Boolean)
                .join(' · ') || '—'}
            </div>
          </div>
        </header>

        {error !== null && (
          <p className="device-info__note device-info__note--err">{error}</p>
        )}

        <Section title="Network Info">
          {identity.localIpv4 !== null && (
            <Row label="Local IP (LAN)" value={identity.localIpv4} />
          )}
          {props?.online !== undefined && (
            <Row
              label="Network State"
              value={props.online ? 'connected' : 'offline'}
              tone={props.online ? 'ok' : 'warn'}
            />
          )}
          {conn?.type !== undefined && <Row label="Connection Type" value={conn.type} />}
          {/* SOTI-supplied downstream bandwidth (kbps) when present;
              otherwise fall back to the Network Information API's
              estimate (Mbps). They measure slightly different things
              so we don't double-show. */}
          {props?.downlinkKbps !== undefined ? (
            <Row
              label="Downlink"
              value={`${(props.downlinkKbps / 1000).toFixed(1)} Mbps`}
              tone="ok"
            />
          ) : conn?.downlinkMbps !== undefined ? (
            <Row label="Downlink" value={`${conn.downlinkMbps.toFixed(1)} Mbps`} tone="ok" />
          ) : null}
          {props?.uplinkKbps !== undefined && (
            <Row label="Uplink" value={`${(props.uplinkKbps / 1000).toFixed(1)} Mbps`} tone="ok" />
          )}
          {conn?.rttMs !== undefined && (
            <Row
              label="RTT"
              value={`${String(conn.rttMs)} ms`}
              {...(conn.rttMs < 200 ? ({ tone: 'ok' } as const) : {})}
            />
          )}
          {props?.metered !== undefined && (
            <Row
              label="Metered"
              value={props.metered ? 'yes (billed by usage)' : 'no'}
              {...(props.metered ? ({ tone: 'warn' } as const) : {})}
            />
          )}
          {(mdmIntegration || props?.vpnConnected !== undefined) && (
            <Row
              label="VPN"
              value={props?.vpnConnected === undefined ? 'true' : String(props.vpnConnected)}
              placeholder={props?.vpnConnected === undefined}
            />
          )}
          {mdmIntegration && (
            <Row
              label="SSID"
              value={props?.ssid ?? 'MyNetworkSSID'}
              placeholder={!props?.ssid}
            />
          )}
          {props?.carrier && <Row label="Carrier" value={props.carrier} />}
          {props?.roaming !== undefined && (
            <Row
              label="Roaming"
              value={props.roaming ? 'yes' : 'no'}
              {...(props.roaming ? ({ tone: 'warn' } as const) : {})}
            />
          )}
          {mdmIntegration && (
            <Row
              label="Signal Strength"
              value={
                props?.signalDbm !== undefined ? `${String(props.signalDbm)} dBm` : '-70 dBm'
              }
              placeholder={props?.signalDbm === undefined}
            />
          )}
        </Section>

        <Section title="Device Info">
          <Row label="Device Name" value={identity.deviceName ?? props?.deviceName ?? 'Unknown device'} placeholder={identity.deviceName === null && !props?.deviceName && mdmIntegration} />
          {props?.manufacturer && (
            <Row label="Manufacturer" value={props.manufacturer} />
          )}
          {props?.model && <Row label="Model" value={props.model} />}
          {formFactor && <Row label="Form Factor" value={formFactor} />}
          <Row label="OS" value={osLabel} placeholder={osLabel === '—' && mdmIntegration} />
          {identity.securityPatch !== null && (
            <Row label="Security patch" value={identity.securityPatch} />
          )}
          {identity.appVersion !== null && (
            <Row label="Launcher build" value={identity.appVersion} />
          )}
          <Row label="Launcher ID" value={identity.deviceId} />
          {identity.androidId !== null && (
            <Row label="Android ID" value={identity.androidId} />
          )}
          {(mdmIntegration || props?.serialNumber) && (
            <Row
              label="Serial Number"
              value={props?.serialNumber ?? 'c298f29c26626041'}
              placeholder={!props?.serialNumber}
            />
          )}
          {mdmIntegration && (
            <Row label="IMEI" value={props?.imei ?? '355561840039244'} placeholder={!props?.imei} />
          )}
          {props?.macAddress && (
            <Row label="MAC Address" value={props.macAddress} />
          )}
          {props?.batteryLevel !== undefined && (
            <Row
              label="Battery"
              value={`${String(Math.round(props.batteryLevel * 100))}%${
                props.batteryCharging === true ? ' (charging)' : ''
              }${
                props.batteryTemperatureC !== undefined
                  ? ` · ${props.batteryTemperatureC.toFixed(1)}°C`
                  : ''
              }`}
              {...(props.batteryLevel < 0.15 && props.batteryCharging !== true
                ? ({ tone: 'warn' } as const)
                : ({ tone: 'ok' } as const))}
            />
          )}
          <Row
            label="Storage"
            value={
              props?.storageUsageBytes !== undefined && props.storageQuotaBytes !== undefined
                ? `${formatBytes(props.storageUsageBytes)} / ${formatBytes(props.storageQuotaBytes)}`
                : '…'
            }
            {...(props?.storageUsageBytes !== undefined ? ({ tone: 'ok' } as const) : {})}
          />
          {props?.hardwareConcurrency !== undefined && (
            <Row label="CPU Cores" value={String(props.hardwareConcurrency)} />
          )}
          {props?.deviceMemoryGb !== undefined && (
            <Row label="Device Memory" value={`${String(props.deviceMemoryGb)} GB`} />
          )}
          {props?.screen && (
            <Row
              label="Screen"
              value={`${String(props.screen.width)} × ${String(props.screen.height)} @${props.screen.pixelRatio.toFixed(1)}x`}
            />
          )}
          {props?.language && <Row label="Locale" value={props.language} />}
          {props?.timezone && <Row label="Timezone" value={props.timezone} />}
        </Section>

        {hasStorage && storage !== null && (
          <Section title="Storage & Memory">
            {storage.internal !== undefined && (
              <Row label="Internal Storage" value={spaceLabel(storage.internal)} tone="ok" />
            )}
            {storage.external !== undefined && (
              <Row label="External Storage" value={spaceLabel(storage.external)} />
            )}
            {storage.memory !== undefined && (
              <Row label="RAM" value={spaceLabel(storage.memory)} />
            )}
          </Section>
        )}

        {mdmIntegration ? (
          <p className="device-info__note">
            Fields marked <span className="device-info__placeholder-pip" aria-hidden /> are
            placeholders; the production agent host replaces them with values from
            the device's MDM agent, where applicable.
          </p>
        ) : (
          <p className="device-info__note">
            Showing only what the device can report on its own. IMEI, Wi-Fi SSID,
            carrier, and signal strength come from an MDM agent — available on
            Enterprise once you opt into the MDM agent integration (Portal →
            Launcher tab → Profile basics → MDM).
          </p>
        )}
      </div>
    </LauncherModal>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="device-info__section">
      <h3 className="device-info__section-title">{title}</h3>
      <div className="device-info__rows">{children}</div>
    </section>
  );
}

function Row({
  label,
  value,
  tone,
  placeholder,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'warn';
  placeholder?: boolean;
}): JSX.Element {
  return (
    <div className="device-info__row">
      <span className="device-info__row-label">
        {label}
        {placeholder && <span className="device-info__placeholder-pip" aria-hidden />}
      </span>
      <span
        className={`device-info__row-value${tone ? ` device-info__row-value--${tone}` : ''}${placeholder ? ' device-info__row-value--placeholder' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Readable OS label. The APK bridge reports a bare Android release ("16"); a
 * browser host reports a UA-derived label ("Android 16", "macOS 10.15.7").
 * Prefix the bare value on an Android device without double-prefixing.
 */
function formatOsLabel(identity: DeviceIdentity): string {
  const os = identity.osVersion;
  if (os === null || os.length === 0) return '—';
  if (identity.androidId !== null && /^\d/.test(os)) return `Android ${os}`;
  return os;
}

function formatBytes(bytes: number): string {
  const gb = bytes / 1_000_000_000;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / 1_000_000;
  return `${mb.toFixed(0)} MB`;
}

/** "X free of Y" when both known; degrades gracefully to whichever side
 *  the agent reported. */
function spaceLabel(area: { availableSpace?: number; totalSpace?: number }): string {
  const { availableSpace, totalSpace } = area;
  if (availableSpace !== undefined && totalSpace !== undefined) {
    return `${formatBytes(availableSpace)} free of ${formatBytes(totalSpace)}`;
  }
  if (totalSpace !== undefined) return formatBytes(totalSpace);
  if (availableSpace !== undefined) return `${formatBytes(availableSpace)} free`;
  return '—';
}
